import React, { memo, useEffect, useState } from 'react';
import { v4 } from 'uuid';

import { useAppSelector } from '../../../store/hooks';
import Font from '../Font';
import Icon from '../Icon';
import {
  AnchoredLeft,
  AnchoredRight,
  CurrentValueWrapper,
  SliderBase,
  SliderContainer,
  SliderCursor,
  SliderProgress,
  SliderStructure,
  SliderWrapper,
  StyledButton,
  StyledRangeInput,
  TickCircle,
  TitleWrapper
} from './styles';

export type SliderProps = {
  buttonName?: string;
  extraText?: string;
  formatter?: (value: number) => number | string;
  hideTickmarks?: boolean;
  initialValue?: number;
  isUnitMeasureVisible?: boolean;
  labelName: string;
  onChange: (value: number) => void;
  tickmarks: number[];
  unitMeasure?: string;
};

const reduceFunc = (selected: number) => (prev: number, curr: number) =>
  Math.abs(curr - selected) < Math.abs(prev - selected) ? curr : prev;

const concatValueToShow = (
  formattedValue: number | string,
  unitMeasure?: string
) => {
  const um = unitMeasure ? ` ${unitMeasure}` : '';
  return `${formattedValue}${um}`;
};
const findTickmark = (
  tickmarks: number[],
  min: number,
  max: number,
  currentValue: number
) => {
  const indexCurrent = tickmarks.findIndex((elm) => elm === currentValue);

  const previous = indexCurrent === 0 ? min : tickmarks[indexCurrent - 1];
  const next =
    indexCurrent === tickmarks.length - 1 ? max : tickmarks[indexCurrent + 1];
  return { next, previous };
};

const findPercentageIndex = (
  containerRef: React.MutableRefObject<null>,
  e: React.DragEvent | React.MouseEvent,
  percentageArr: number[]
) => {
  if (!containerRef.current) return 0;

  const containerRect = (
    containerRef.current as HTMLDivElement
  ).getBoundingClientRect();
  const xPercentage =
    ((e.clientX - containerRect.left) * 100) / containerRect.width;
  const closestStep = percentageArr.reduce(
    (prev, curr) =>
      Math.abs(curr - xPercentage) < Math.abs(prev - xPercentage) ? curr : prev,
    0
  );

  return percentageArr.indexOf(closestStep);
};

const findPercentage = (currentValue: number, max: number, min: number) => {
  if (currentValue === 0) {
    return 0;
  }
  return ((currentValue - min) / (max - min)) * 100;
};

const findCurrentValue = (initialValue: number, tickmarks: number[]) => {
  if (tickmarks.includes(initialValue)) {
    return initialValue;
  }
  return tickmarks[0];
};

const SliderInput: React.FC<SliderProps> = memo(
  ({
    buttonName,
    extraText,
    formatter = (val: any) => val,
    hideTickmarks = false,
    initialValue,
    isUnitMeasureVisible,
    labelName,
    onChange,
    tickmarks,
    unitMeasure
  }) => {
    const { widgetType } = useAppSelector((state) => state.app);
    const min = tickmarks[0];
    const max = tickmarks[tickmarks.length - 1];
    const percentageArr = tickmarks.map(
      (value) => ((value - min) / (max - min)) * 100
    );

    const containerRef = React.useRef(null);
    const [percentage, setPercentage] = useState(0);
    const [currentValue, setCurrentValue] = useState(0);
    const [formattedValue, setFormattedValue] = useState('');

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const current: any = e.target.value;
      const closestStep = tickmarks.reduce(reduceFunc(current));
      setCurrentValue(closestStep);
    };

    const handleCircleClick = (value: number) => {
      setCurrentValue(value);
      onChange(value);
    };

    const findPrevNext = (increment: boolean) => {
      const { next, previous } = findTickmark(
        tickmarks,
        min,
        max,
        currentValue
      );
      const clickValue = increment ? next : previous;
      handleCircleClick(clickValue);
    };

    const handleAction = (
      e: React.DragEvent | React.MouseEvent,
      triggerChange?: boolean
    ) => {
      e.preventDefault();
      const index = findPercentageIndex(containerRef, e, percentageArr);

      setCurrentValue(tickmarks[index]);
      if (triggerChange) {
        onChange(tickmarks[index]);
      }
    };

    useEffect(() => {
      setPercentage(findPercentage(currentValue, max, min));
    }, [tickmarks, currentValue]);

    useEffect(() => {
      if (initialValue && tickmarks && tickmarks.length > 0) {
        setCurrentValue(findCurrentValue(initialValue, tickmarks));
      }
    }, [initialValue]);

    useEffect(() => {
      setFormattedValue(
        concatValueToShow(formatter(currentValue), unitMeasure)
      );
    }, [currentValue, unitMeasure]);

    const unitMeasureBelow = isUnitMeasureVisible ? unitMeasure : '';

    const handleKeyDown =
      (value: number) => (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
          handleCircleClick(value);
        }
      };

    return (
      <SliderStructure $hasSlider={Boolean(tickmarks.length > 0)}>
        <TitleWrapper>
          <Font
            variant={widgetType === 'modal' ? 'bodyRegular' : 'bodyLRegular'}
          >
            {`${labelName}${extraText ?? ''}`}
          </Font>
        </TitleWrapper>
        <CurrentValueWrapper>
          <Font variant={widgetType === 'modal' ? 'bodyBold' : 'bodyLBold'}>
            {formattedValue}
          </Font>
        </CurrentValueWrapper>
        {tickmarks.length > 0 && (
          <>
            <StyledRangeInput // Hidden input
              aria-label={labelName}
              aria-valuemax={max}
              aria-valuemin={min}
              aria-valuenow={currentValue}
              data-testid="input-currentvalue"
              max={max}
              min={min}
              onChange={handleChange}
              type="range"
              value={currentValue}
            />

            <SliderContainer>
              <StyledButton
                data-testid="less_button"
                name={`${buttonName}-minus`}
                onClick={() => findPrevNext(false)}
                type="button"
              >
                <Icon
                  iconColor="white"
                  iconName="InterfaceLess"
                  iconSize="xs"
                />
              </StyledButton>
              <SliderWrapper
                data-testid="slider-wrapper"
                onClick={(e) => handleAction(e, true)}
              >
                <SliderBase ref={containerRef}>
                  <SliderProgress $progress={percentage} />
                  {!hideTickmarks &&
                    tickmarks?.map((value, index) => (
                      <TickCircle
                        $left={((value - min) / (max - min)) * 100}
                        aria-label={`${value}`}
                        data-testid={`test-cirle-input-${index}`}
                        key={`tickmark_${v4()}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCircleClick(value);
                        }}
                        onKeyDown={handleKeyDown(value)}
                        tabIndex={0}
                      />
                    ))}
                  <SliderCursor
                    $left={percentage}
                    data-testid="cursor-element"
                    onDrag={(e) => handleAction(e)}
                    onDragEnd={(e) => handleAction(e, true)}
                    onDragStart={(e) => {
                      const elem = e.target as Element;
                      e.dataTransfer.setDragImage(
                        elem,
                        window.outerWidth,
                        window.outerHeight
                      );
                    }}
                  />
                </SliderBase>
              </SliderWrapper>
              <StyledButton
                data-testid="add_button"
                name={`${buttonName}-plus`}
                onClick={() => findPrevNext(true)}
                type="button"
              >
                <Icon iconColor="white" iconName="InterfaceAdd" iconSize="xs" />
              </StyledButton>
            </SliderContainer>
            <AnchoredLeft $widgetType={widgetType}>
              <Font variant="bodyRegular">
                {formatter(min)} {unitMeasureBelow}
              </Font>
            </AnchoredLeft>
            <AnchoredRight $widgetType={widgetType}>
              <Font variant="bodyRegular">
                {formatter(max)} {unitMeasureBelow}
              </Font>
            </AnchoredRight>
          </>
        )}
      </SliderStructure>
    );
  }
);

export default SliderInput;
